import { DATA_WORKSPACE_ENABLED } from "../config/dataWorkspaceFeature";
import { workspaceHref } from "../scenes/data-workspace/utils/workspaceSource";
import { mergeCleaningJob, normalizeCleaningJob } from "../scenes/data-workspace/utils/cleaningJobState";

export const CLEANING_JOB_TYPE = "data_cleaning";
export const CLEANING_HISTORY_LIMIT = 100;
export const CLEANING_TOAST_STORAGE_KEY = "pm.cleaningJobToasts";

/** Same codes as the cleaning progress card. Raw codes are never shown. */
export const CLEANING_JOB_ERROR_CODES = new Set([
  "STALE_PREVIEW",
  "TIMEOUT",
  "WORKER_RESTARTED",
  "QUEUE_WAIT_EXPIRED",
  "QUEUE_UNAVAILABLE",
  "CLEANING_JOB_ACTIVE",
  "CLEANING_JOB_LIMIT",
]);

const count = (value) => (typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null);

export function cleaningJobErrorKey(code) {
  return CLEANING_JOB_ERROR_CODES.has(code)
    ? `dataWorkspace:cleaningJob.errors.${code}`
    : "dataWorkspace:cleaningJob.errors.default";
}

function allowResult(status, result) {
  if (status !== "completed" || !result || typeof result !== "object" || Array.isArray(result)) return null;
  const net = count(result.affected_cells);
  const columns = count(result.columns_count);
  const hasSignal = net != null || columns != null || result.no_op === true || typeof result.change_id === "string";
  if (!hasSignal) return null;
  return {
    affected_cells: net,
    columns_count: columns,
    change_id: typeof result.change_id === "string" ? result.change_id : null,
    no_op: result.no_op === true,
  };
}

function panelShape(normalized, raw, estimate) {
  return {
    id: normalized.jobId,
    job_type: CLEANING_JOB_TYPE,
    status: normalized.status,
    progress: normalized.progress ?? 0,
    queued_at: raw?.queued_at ?? null,
    started_at: raw?.started_at ?? null,
    completed_at: raw?.completed_at ?? null,
    dataset_id: normalized.datasetId,
    source_kind: normalized.sourceKind,
    dataset_name: typeof raw?.dataset_name === "string" && raw.dataset_name.trim() ? raw.dataset_name.trim() : null,
    affected_cells: estimate,
    estimated_affected_cells: estimate,
    stage: normalized.stage,
    error_code: normalized.errorCode,
    result: normalized.status === "completed" ? allowResult("completed", normalized.result) : null,
  };
}

/** List/GET row. Preview `affected_cells` stays an estimate; net counts come only from `result`. */
export function panelJobFromApi(raw) {
  if (!DATA_WORKSPACE_ENABLED) return null;
  if (!raw || typeof raw !== "object") return null;
  const normalized = normalizeCleaningJob(raw);
  if (!normalized) return null;
  const estimate = count(raw.estimated_affected_cells ?? raw.affected_cells);
  return panelShape(normalized, raw, estimate);
}

export function panelJobFromEvent(event, payload) {
  if (!DATA_WORKSPACE_ENABLED) return null;
  const normalized = normalizeCleaningJob(payload, { event });
  if (!normalized) return null;
  return { ...panelShape(normalized, payload, null), source_kind: payload?.source_kind || payload?.source?.kind || undefined };
}

export function mergePanelCleaningJob(previous, incoming) {
  if (!incoming) return previous;
  if (!previous) return incoming;
  const merged = mergeCleaningJob(normalizeCleaningJob(previous), normalizeCleaningJob({ ...incoming, source_kind: incoming.source_kind || previous.source_kind }));
  if (!merged) return previous;
  const estimate = count(
    incoming.estimated_affected_cells ?? incoming.affected_cells ?? previous.estimated_affected_cells ?? previous.affected_cells
  );
  const result = merged.status === "completed"
    ? allowResult("completed", merged.result) || allowResult("completed", previous.result)
    : null;
  return {
    ...previous,
    id: merged.jobId,
    job_type: CLEANING_JOB_TYPE,
    status: merged.status,
    progress: merged.progress ?? previous.progress ?? 0,
    dataset_id: merged.datasetId,
    source_kind: merged.sourceKind,
    dataset_name: incoming.dataset_name || previous.dataset_name || null,
    queued_at: incoming.queued_at || previous.queued_at || null,
    started_at: incoming.started_at ?? previous.started_at ?? null,
    completed_at: incoming.completed_at ?? previous.completed_at ?? null,
    stage: merged.stage,
    error_code: merged.errorCode ?? previous.error_code ?? null,
    affected_cells: estimate,
    estimated_affected_cells: estimate,
    result,
  };
}

export function buildCleaningQueuedJob({ id, datasetId, datasetName, queuedAt, source } = {}) {
  if (!DATA_WORKSPACE_ENABLED) return null;
  if (id == null || datasetId == null) return null;
  const job = {
    id: String(id),
    job_type: CLEANING_JOB_TYPE,
    status: "queued",
    progress: 0,
    dataset_id: String(datasetId),
    ...(source ? { source_kind: source.kind } : {}),
    queued_at: queuedAt || new Date().toISOString(),
  };
  if (typeof datasetName === "string" && datasetName.trim()) job.dataset_name = datasetName.trim();
  return job;
}

export function dispatchCleaningJobQueued(input) {
  const job = buildCleaningQueuedJob(input);
  if (!job || typeof window === "undefined") return null;
  window.dispatchEvent(new CustomEvent("cleaning:job:queued", { detail: { job } }));
  return job;
}

export function compareJobsByQueuedDesc(a, b) {
  return new Date(b.queued_at || 0) < new Date(a.queued_at || 0) ? -1 : 1;
}

export function applyClearedAt(jobs, clearedAt) {
  const cutoff = Number(clearedAt) || 0;
  return jobs.filter((job) => {
    if (["queued", "processing"].includes(job.status)) return true;
    const jobTime = new Date(job.completed_at || job.queued_at || job.created_at || 0).getTime();
    return jobTime > cutoff;
  });
}

export function combineJobFeeds({ statsJobs = [], mlJobs = [], cleaningJobs = [], previous = [], clearedAt = 0 } = {}) {
  const mapped = cleaningJobs.map(panelJobFromApi).filter(Boolean);
  let combined = [...statsJobs, ...mlJobs, ...mapped].map((job) => {
    if (job.job_type !== CLEANING_JOB_TYPE) return job;
    const old = previous.find((item) => item.id === job.id && item.job_type === CLEANING_JOB_TYPE);
    return old ? mergePanelCleaningJob(old, job) : job;
  });
  const ids = new Set(combined.map((job) => job.id));
  for (const old of previous) {
    if (DATA_WORKSPACE_ENABLED && old.job_type === CLEANING_JOB_TYPE && !ids.has(old.id) && ["queued", "processing"].includes(old.status)) {
      combined.push(old);
    }
  }
  return applyClearedAt(combined.sort(compareJobsByQueuedDesc).slice(0, 20), clearedAt);
}

export function applyCleaningSocket(previous, event, payload) {
  const incoming = panelJobFromEvent(event, payload);
  if (!incoming) return { jobs: previous, changed: null, unknown: false };
  const existing = previous.find((job) => job.id === incoming.id && job.job_type === CLEANING_JOB_TYPE);
  if (!existing) return { jobs: previous, changed: null, unknown: true };
  const changed = mergePanelCleaningJob(existing, incoming);
  return {
    jobs: previous.map((job) => (job.id === incoming.id && job.job_type === CLEANING_JOB_TYPE ? changed : job)),
    changed,
    unknown: false,
  };
}

export function cleaningTerminalTransitions(previous, next) {
  const prevById = new Map(
    (previous || []).filter((job) => job.job_type === CLEANING_JOB_TYPE).map((job) => [job.id, job])
  );
  const found = [];
  for (const job of next || []) {
    if (job.job_type !== CLEANING_JOB_TYPE || !["completed", "failed"].includes(job.status)) continue;
    const old = prevById.get(job.id);
    if (!old || !["queued", "processing"].includes(old.status) || old.status === job.status) continue;
    found.push(job);
  }
  return found;
}

export function readCleaningToastIds() {
  if (typeof sessionStorage === "undefined") return new Set();
  try {
    const raw = JSON.parse(sessionStorage.getItem(CLEANING_TOAST_STORAGE_KEY) || "[]");
    return new Set(Array.isArray(raw) ? raw.map(String) : []);
  } catch {
    return new Set();
  }
}

export function rememberCleaningToastId(ids, id) {
  ids.add(String(id));
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(CLEANING_TOAST_STORAGE_KEY, JSON.stringify([...ids].slice(-200)));
  } catch {
    /* storage can be unavailable */
  }
}

export function isViewingCleaningDataset(pathname, datasetId) {
  if (!datasetId) return false;
  const parts = String(pathname || "").split("/").filter(Boolean);
  const index = parts.indexOf("data-workspace");
  if (index < 0) return false;
  try {
    return decodeURIComponent(parts[index + 1] || "") === String(datasetId);
  } catch {
    return parts[index + 1] === String(datasetId);
  }
}

export function cleaningJobTitle(job, t) {
  const name = typeof job?.dataset_name === "string" ? job.dataset_name.trim() : "";
  return name ? t("jobs.cleaning.title", { name }) : t("jobs.cleaning.untitled");
}

export function formatCleaningCount(language, value) {
  const base = String(language || "tr").split("-")[0];
  return new Intl.NumberFormat(base).format(value);
}

/** Running text uses the preview cell estimate. Completed text uses only `result`. */
export function cleaningJobDetail(job, t, language) {
  if (!job || job.job_type !== CLEANING_JOB_TYPE) return "";
  if (job.status === "completed") {
    if (job.result?.no_op === true) return t("jobs.cleaning.noChange");
    const net = count(job.result?.affected_cells);
    if (net == null) return "";
    return t("jobs.cleaning.cellsChanged", { count: net, value: formatCleaningCount(language, net) });
  }
  if (job.status === "queued" || job.status === "processing") {
    const estimate = count(job.estimated_affected_cells ?? job.affected_cells);
    if (estimate == null) return "";
    return t("jobs.cleaning.estimatedCells", { count: estimate, value: formatCleaningCount(language, estimate) });
  }
  if (job.status === "failed") return t(cleaningJobErrorKey(job.error_code));
  return "";
}

export function cleaningJobHref(job) {
  if (!job || job.job_type !== CLEANING_JOB_TYPE) return null;
  if (job.status !== "completed" && job.status !== "failed") return null;
  if (job.dataset_id == null || job.id == null) return null;
  return workspaceHref({ kind: job.source_kind || "import_job", id: job.dataset_id }, { cleaningJob: job.id });
}

export function shouldLoadCleaningHistory(filters = {}) {
  const category = filters.category || "all";
  if (category !== "all" && category !== "cleaning") return false;
  if (category === "all" && filters.test_id && filters.test_id !== "all") return false;
  if (category === "all" && (filters.minCost || filters.maxCost)) return false;
  return true;
}

export function filterCleaningHistoryJobs(jobs, filters = {}) {
  const from = filters.dateFrom ? new Date(filters.dateFrom).getTime() : null;
  const to = filters.dateTo ? new Date(filters.dateTo).getTime() : null;
  return (jobs || []).filter((job) => {
    const time = new Date(job.queued_at || job.completed_at || 0).getTime();
    if (from != null && !Number.isNaN(from) && time < from) return false;
    if (to != null && !Number.isNaN(to) && time > to) return false;
    return true;
  });
}

export function pageHistoryJobs({
  category = "all",
  page = 0,
  pageSize = 20,
  statsJobs = [],
  statsTotal = 0,
  cleaningJobs = [],
  filters = {},
} = {}) {
  const filtered = filterCleaningHistoryJobs(cleaningJobs, filters);
  const size = Math.min(CLEANING_HISTORY_LIMIT, Math.max(1, Number(pageSize) || 20));
  if (category === "cleaning") {
    const start = page * size;
    return { jobs: filtered.slice(start, start + size), total: filtered.length };
  }
  if (category === "stats" || category === "ml") {
    return { jobs: statsJobs, total: statsTotal };
  }
  const extras = page === 0 ? filtered : [];
  return {
    jobs: [...statsJobs, ...extras].sort(compareJobsByQueuedDesc),
    total: (Number(statsTotal) || 0) + filtered.length,
  };
}
